/* feature:slowmo — pure logic of the director camera and bullet time (no DOM, no three.js; tested in tests/slowmo.test.js).
   Highlights: sim events -> scored moments { id, kind, label, t, t1, x, y, z, r, w, bullet } in game seconds.
   Front: where the two armies meet, estimated from the unit buffer.
   Director: picks shots (Totale, Front-Fahrt, Held, Orbit) with a minimum hold time and smooth moves or hard cuts.
   Camera time is real time (an orbit keeps turning during bullet time); interest decays in game time. */

export const BULLET_SCALE = 0.25;
export const HL_LABEL = {
  nuke: 'Atombombe', explosion: 'Explosion', mass: 'Massensterben', giant: 'Riese im Kampf',
  giantDeath: 'Ein Riese fällt', building: 'Gebäude zerstört', contact: 'Erster Kontakt',
};
export const SHOT_LABEL = { totale: 'Totale', front: 'Fahrt an der Front', hero: 'Nah am Helden', orbit: 'Orbit' };

// sim event types (sim.worker.js): EV_DEATH 1, EV_EXPL 2, EV_SWING 4, EV_SLAM 6, EV_BDEAD 8; 8 floats per event
const EVS = 8, KIND_NUKE = 3;
const W_CAP = 12, MERGE_DT = 1.5, HALF_LIFE = 2.5;

const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
export const smoothstep = x => { const t = clamp(x, 0, 1); return t * t * (3 - 2 * t); };
const wrap = a => { while (a > Math.PI) a -= 2 * Math.PI; while (a < -Math.PI) a += 2 * Math.PI; return a; };
const lerpAng = (a, b, t) => a + wrap(b - a) * t;

/* ---------------- highlights ---------------- */
/** score one batch of sim events; info: { isGiant(i), size(i) } (both optional) */
export function scanEvents(ev, n, t, info = {}) {
  const out = [];
  if (!n) return out;
  const isGiant = info.isGiant || (() => false), size = info.size || (() => 1);
  let nd = 0;
  const dx = [], dz = [], ex = [];
  for (let k = 0; k < n; k++) {
    const o = k * EVS, type = ev[o];
    if (type === 1) {
      const i = ev[o + 1] | 0; dx.push(ev[o + 2]); dz.push(ev[o + 4]); nd++;
      if (isGiant(i)) out.push(hl('giantDeath', t, ev[o + 2], ev[o + 3], ev[o + 4], Math.max(4, size(i) * 2), 6, true, { unit: i }));
    } else if (type === 2) {
      const aoe = ev[o + 4], kind = ev[o + 5];
      if (kind === KIND_NUKE) { out.push(hl('nuke', t, ev[o + 1], ev[o + 2], ev[o + 3], aoe, 10, true)); ex.unshift(ev[o + 1], ev[o + 2], ev[o + 3], -aoe); }
      else if (aoe >= 4 && ex.length < 64 * 4) ex.push(ev[o + 1], ev[o + 2], ev[o + 3], aoe);
    } else if (type === 4) {
      const R = ev[o + 4];
      if (R >= 6) out.push(hl('giant', t, ev[o + 1], 0, ev[o + 2], R, 0.6 + R * 0.05, false));
    } else if (type === 6) out.push(hl('giant', t, ev[o + 1], 0, ev[o + 2], ev[o + 3], 1.2, false));
    else if (type === 8) out.push(hl('building', t, ev[o + 2], ev[o + 3], ev[o + 4], 8, 2.5, false, { building: ev[o + 1] | 0 }));
  }
  // explosions: weight by size and by the deaths they caused; a big blast in a crowd earns bullet time
  const used = nd ? new Uint8Array(nd) : null;
  for (let e = 0; e < ex.length; e += 4) {
    const x = ex[e], y = ex[e + 1], z = ex[e + 2], nuke = ex[e + 3] < 0, aoe = Math.abs(ex[e + 3]), R = aoe * 1.25, R2 = R * R;
    let d = 0;
    for (let k = 0; k < nd; k++) { const ax = dx[k] - x, az = dz[k] - z; if (ax * ax + az * az <= R2) { d++; used[k] = 1; } }
    if (nuke) { const h = out.find(h => h.kind === 'nuke' && h.x === x && h.z === z); if (h) h.deaths = d; continue; } // nukes are first in ex
    out.push(hl('explosion', t, x, y, z, aoe, Math.min(W_CAP, (aoe >= 8 ? 2.5 : 1) + d * 0.12), aoe >= 8 && d >= 8, { deaths: d }));
  }
  // many deaths on a small patch (not already explained by an explosion)
  if (nd >= 6) {
    const C = 12, cells = new Map();
    for (let k = 0; k < nd; k++) {
      if (used[k]) continue;
      const key = Math.floor(dx[k] / C) * 100003 + Math.floor(dz[k] / C);
      let c = cells.get(key); if (!c) cells.set(key, c = [0, 0, 0]);
      c[0]++; c[1] += dx[k]; c[2] += dz[k];
    }
    for (const c of cells.values()) if (c[0] >= 6) out.push(hl('mass', t, c[1] / c[0], 0, c[2] / c[0], 10, Math.min(6, c[0] / 5), false, { deaths: c[0] }));
  }
  return out;
}
function hl(kind, t, x, y, z, r, w, bullet, extra) {
  return { id: 0, kind, label: HL_LABEL[kind], t, t1: t, x, y, z, r, w, bullet: !!bullet, ...extra };
}

/** interest of a highlight at game time t (decays with a half-life in game seconds) */
export const interest = (h, t) => h.w * Math.pow(0.5, Math.max(0, t - h.t1) / HALF_LIFE);

/** time-ordered highlight list; nearby repeats of the same kind merge into one growing entry */
export function createHighlightLog({ max = 500 } = {}) {
  let list = [], nextId = 1;
  return {
    /** returns the stored entry (new or merged) */
    add(h) {
      for (let k = list.length - 1, seen = 0; k >= 0 && seen < 40; k--, seen++) {
        const e = list[k];
        if (h.t - e.t1 > MERGE_DT) break;
        if (e.kind !== h.kind || e.kind === 'nuke') continue;
        const R = Math.max(12, e.r, h.r);
        if ((e.x - h.x) ** 2 + (e.z - h.z) ** 2 > R * R) continue;
        e.w = Math.min(W_CAP, Math.max(e.w, h.w) + Math.min(e.w, h.w) * 0.5);
        e.t1 = h.t; e.r = Math.max(e.r, h.r);
        if (h.deaths) e.deaths = (e.deaths || 0) + h.deaths;
        return e;
      }
      const e = { ...h, id: nextId++ };
      list.push(e);
      if (list.length > max) list = list.slice(list.length - max);
      return e;
    },
    /** all entries (oldest first), optionally only since game time `since` and at least weight `minWeight` */
    list({ since = -Infinity, minWeight = 0 } = {}) { return list.filter(e => e.t1 >= since && e.w >= minWeight).map(e => ({ ...e })); },
    /** live entries touched within the last `window` game seconds (newest first, not copied) */
    recent(t, window = 12) { const r = []; for (let k = list.length - 1; k >= 0 && t - list[k].t1 <= window; k--) r.push(list[k]); return r; },
    clear() { list = []; },
    get size() { return list.length; },
  };
}

/* ---------------- front line ---------------- */
/** unit buffer (10 floats per unit: x, y, z, rot, …, alive at +9), team 0 = [0, split) */
export function estimateFront(b, N, split, maxSamples = 1500) {
  if (!b || !N || b.length < N * 10) return null;
  const step = Math.max(1, Math.floor(N / maxSamples));
  const c = [[0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < N; i += step) { const o = i * 10; if (!b[o + 9]) continue; const C = c[i < split ? 0 : 1]; C[0] += b[o]; C[1] += b[o + 2]; C[2]++; }
  if (!c[0][2] || !c[1][2]) return null;
  const x0 = c[0][0] / c[0][2], z0 = c[0][1] / c[0][2], x1 = c[1][0] / c[1][2], z1 = c[1][1] / c[1][2];
  let nx = x1 - x0, nz = z1 - z0; const nl = Math.hypot(nx, nz);
  if (nl < 1) { nx = 1; nz = 0; } else { nx /= nl; nz /= nl; }
  const fx = -nz, fz = nx;
  const p0 = [], p1 = [];
  let ext = 0; const mx = (x0 + x1) / 2, mz = (z0 + z1) / 2;
  for (let i = 0; i < N; i += step) {
    const o = i * 10; if (!b[o + 9]) continue;
    const ax = b[o] - x0, az = b[o + 2] - z0;
    (i < split ? p0 : p1).push(ax * nx + az * nz);
    const e = Math.hypot(b[o] - mx, b[o + 2] - mz); if (e > ext) ext = e;
  }
  p0.sort((a, b) => a - b); p1.sort((a, b) => a - b);
  const q = (a, f) => a[Math.min(a.length - 1, Math.floor(a.length * f))];
  const e0 = q(p0, 0.85), e1 = q(p1, 0.15), mid = (e0 + e1) / 2, gap = e1 - e0;
  // lateral extent of everyone close to the front
  const band = Math.max(12, Math.abs(gap) * 0.6 + 6);
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < N; i += step) {
    const o = i * 10; if (!b[o + 9]) continue;
    const ax = b[o] - x0, az = b[o + 2] - z0;
    if (Math.abs(ax * nx + az * nz - mid) > band) continue;
    const s = ax * fx + az * fz; if (s < lo) lo = s; if (s > hi) hi = s;
  }
  if (lo > hi) { lo = -10; hi = 10; }
  const sc = (lo + hi) / 2;
  return {
    x: x0 + nx * mid + fx * sc, z: z0 + nz * mid + fz * sc, nx, nz, fx, fz,
    half: Math.max(6, (hi - lo) / 2), gap, contact: gap < 8,
    cx: mx, cz: mz, ext: Math.max(20, ext),
  };
}

/* ---------------- bullet time ---------------- */
const BT_IN = 0.18, BT_HOLD = 1.1, BT_OUT = 1.4;
export const BULLET_DURATION = BT_IN + BT_HOLD + BT_OUT;
/** 0..1 depth of the slow-down, `age` in real seconds since the trigger */
export function bulletEnvelope(age) {
  if (age < 0 || age >= BULLET_DURATION) return 0;
  if (age < BT_IN) return smoothstep(age / BT_IN);
  if (age < BT_IN + BT_HOLD) return 1;
  return 1 - smoothstep((age - BT_IN - BT_HOLD) / BT_OUT);
}
/** requested scale during bullet time: blend from the user's scale to 0.25x and back (never speeds up) */
export const bulletScale = (user, age) => { const lo = Math.min(user, BULLET_SCALE); return user + (lo - user) * bulletEnvelope(age); };

/* ---------------- director ---------------- */
const DUR = { totale: 5.5, front: 7.5, hero: 6.5, orbit: 6 };

/**
 * update(dt, S) per frame (dt = real seconds). Scene S:
 *  t: game time, log: highlight log, front: estimateFront() | null, center: { x, z, ext },
 *  cur: current camera { tx, ty, tz, yaw, pitch, dist }, ground(x, z),
 *  unit(i): { x, y, z, h, rot, alive } | null, hero(x, z): unit index | -1 (best unit to follow near a spot, -1 = none)
 * returns { pose, cut, shot } — pose is the camera goal; cut = snap there instead of moving.
 */
export function createDirector({ minHold = 2.5, blend = 1.8 } = {}) {
  let shot = null, prev = null, lastUrgent = 0, flip = 1, shown = new WeakSet(); // shown: highlights already on screen
  const D = {
    get shot() { return shot; },
    reset() { shot = null; prev = null; lastUrgent = 0; shown = new WeakSet(); },
    /** jump to a highlight now (e.g. from a replay timeline) */
    focus(h, S, cut = true) { start(shotFor(h, S), S, cut); },
    update(dt, S) {
      if (shot) shot.age += dt;
      const best = bestHighlight(S);
      if (!shot) start(rotate(S, best), S, true);
      else {
        const urgent = best && best.h.bullet && best.h.id > lastUrgent && best.h !== shot.subject;
        if (urgent) { lastUrgent = best.h.id; start(shotFor(best.h, S), S, true); }
        else if (shot.age >= minHold) {
          const curI = shot.subject ? interest(shot.subject, S.t) : 0;
          if (best && !shown.has(best.h) && best.i > curI * 1.4 + 0.35 && best.i > 0.8) start(shotFor(best.h, S), S, false);
          else if (shot.age >= shot.dur || shot.lost) start(rotate(S, best), S, false);
        }
      }
      let pose = shotPose(shot, S);
      const cut = shot.cut; shot.cut = false;
      if (!cut && shot.age < blend) pose = lerpPose(shot.from, pose, smoothstep(shot.age / blend));
      return { pose, cut, shot };
    },
  };
  function start(s, S, cut) {
    if (shot) prev = shot.type;
    s.age = 0; s.from = { ...S.cur };
    // far jumps are hard cuts, nearby ones are camera moves
    const p = shotPose({ ...s }, S);
    const far = Math.hypot(p.tx - S.cur.tx, p.tz - S.cur.tz) > Math.max(90, (p.dist + S.cur.dist) * 1.1);
    s.cut = cut || far;
    if (s.subject) { shown.add(s.subject); if (s.subject.bullet) lastUrgent = Math.max(lastUrgent, s.subject.id); }
    shot = s;
  }
  function bestHighlight(S) {
    let best = null;
    for (const h of S.log.recent(S.t)) { const i = interest(h, S.t); if (!best || i > best.i) best = { h, i }; }
    return best;
  }
  function shotFor(h, S) {
    if (h.kind === 'giant') {
      const i = S.hero(h.x, h.z, true);
      if (i >= 0) return mk('hero', h, S, { unit: i });
    }
    if (h.kind === 'contact' && S.front) return mk('front', h, S);
    return mk('orbit', h, S);
  }
  // nothing pressing: vary between wide, front, hero and the best recent spot
  function rotate(S, best) {
    const cur = shot ? shot.type : '', F = S.front;
    const score = {
      totale: 0.6 + (cur === 'hero' || cur === 'orbit' ? 0.5 : 0),
      front: F ? (F.contact ? 1.2 : 0.35) : -9,
      hero: S.hero(F ? F.x : S.center.x, F ? F.z : S.center.z, true) >= 0 ? (F && F.contact ? 0.9 : 0.5) : -9,
      orbit: best ? Math.min(2, best.i) * (shown.has(best.h) ? 0.5 : 1) : -9,
    };
    if (cur) score[cur] -= 1;
    if (prev) score[prev] -= 0.4;
    let type = 'totale';
    for (const k of Object.keys(score)) if (score[k] > score[type]) type = k;
    if (type === 'orbit') return mk('orbit', best.h, S);
    if (type === 'hero') return mk('hero', null, S, { unit: S.hero(F ? F.x : S.center.x, F ? F.z : S.center.z, true) });
    return mk(type, null, S);
  }
  function mk(type, subject, S, extra) {
    flip = -flip;
    return { type, subject, label: SHOT_LABEL[type] + (subject ? ': ' + subject.label : ''), dur: DUR[type], yaw0: S.cur.yaw, dir: flip,
      F: S.front ? { ...S.front } : null, C: { ...S.center }, cut: false, lost: false, last: null, ...extra };
  }
  return D;
}

function shotPose(s, S) {
  const a = s.age || 0;
  if (s.type === 'hero') {
    const u = S.unit(s.unit);
    if (!s.lost && u && u.alive) s.last = { x: u.x, y: u.y, z: u.z, h: u.h, rot: u.rot }; else s.lost = true; // a fallen hero's slot may respawn elsewhere
    const v = s.last;
    if (v) {
      if (s.yawU === undefined) s.yawU = v.rot + Math.PI * 0.8; // over the shoulder, then slowly around
      return { tx: v.x, ty: v.y + v.h * 0.55, tz: v.z, yaw: s.yawU + 0.12 * a * s.dir, pitch: 0.3, dist: clamp(v.h * 3.2 + 5, 7, 80) };
    }
    return totalePose(s, S, a);
  }
  if (s.type === 'orbit' && s.subject) {
    const h = s.subject, r = h.r || 6;
    return { tx: h.x, ty: S.ground(h.x, h.z) + Math.min(r * 0.25, 8), tz: h.z, yaw: s.yaw0 + 0.28 * a * s.dir, pitch: 0.42 + 0.08 * Math.sin(a * 0.4), dist: clamp(r * 2.4 + 16, 18, 240) };
  }
  if (s.type === 'front' && s.F) {
    const F = s.F, u = smoothstep(a / s.dur), lat = (u * 2 - 1) * F.half * 0.8 * s.dir;
    const x = F.x + F.fx * lat, z = F.z + F.fz * lat;
    // camera behind team 0's line, turned a little against the direction of travel
    const k = 0.45 * s.dir, vx = -F.nx * Math.cos(k) - F.fx * Math.sin(k), vz = -F.nz * Math.cos(k) - F.fz * Math.sin(k);
    return { tx: x, ty: S.ground(x, z) + 1.5, tz: z, yaw: Math.atan2(vx, vz), pitch: 0.36, dist: clamp(F.half * 0.35 + 22, 22, 70) };
  }
  return totalePose(s, S, a);
}
function totalePose(s, S, a) {
  const C = s.C || S.center;
  return { tx: C.x, ty: S.ground(C.x, C.z), tz: C.z, yaw: s.yaw0 + 0.035 * a, pitch: 0.86, dist: clamp(C.ext * 1.25 + 20, 40, 900) };
}
export function lerpPose(a, b, t) {
  return { tx: a.tx + (b.tx - a.tx) * t, ty: a.ty + (b.ty - a.ty) * t, tz: a.tz + (b.tz - a.tz) * t,
    yaw: lerpAng(a.yaw, b.yaw, t), pitch: a.pitch + (b.pitch - a.pitch) * t, dist: a.dist * Math.pow(b.dist / Math.max(1e-3, a.dist), t) };
}
